import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import ReactDOM from 'react-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BooksSection } from '../components/BooksSection';
import { useLanguage } from '../i18n/LanguageContext';
import { FileText, ClipboardCheck, BookOpen, Video, CheckCircle, XCircle, Send, Search, UserCheck, Handshake, Upload, X, AlertCircle, LoaderIcon, ShieldIcon, ClockIcon, Ship, Shuffle, Copy, Anchor, Globe, MailX, TrendingUp, ShieldOff, Ban, FileX, Wrench, ArrowRight } from 'lucide-react';
import { submitForm } from '../utils/submitForm';
/* ══════════════════════════════════════════════════
   BILINGUAL CONTENT
   ══════════════════════════════════════════════════ */
const pageContent = {
  en: {
    heroTitle1: 'Your experience is not the problem.',
    heroTitle2: 'Your presentation is.',
    heroDesc: 'Most offshore candidates get rejected not because they lack sea time — but because their CV is unclear, unstructured, or poorly presented. Elvora helps you get taken seriously.',
    viewServices: 'View Services',
    subtitle: 'Professional offshore CV, document, and interview preparation services.',
    heroImgAlt: 'Offshore oil platform at sunset',
    problemBadge: 'The Problem',
    problemTitle: 'Why Strong Engineers Get Ignored',
    problemDesc: "Recruiters scan a CV in under 10 seconds. If your document doesn't pass that scan, your experience becomes invisible — regardless of your qualifications.",
    counterUnit: 'sec',
    counterDesc: 'Average time offshore recruiters spend reviewing a CV',
    ifYourDoc: 'If your document:',
    hidesVessel: 'Hides vessel type',
    dpHours: "Doesn't clearly show DP hours",
    mixesContracts: 'Mixes up contracts',
    noOffshoreFormat: 'Lacks offshore formatting',
    looksGeneric: 'Looks generic',
    youAre: 'You are',
    invisible: 'invisible',
    elvoraPromise: "Elvora doesn't promise shortcuts. We structure your profile correctly.",
    servicesBadge: 'Services & Pricing',
    servicesTitle: 'Choose Your Upgrade Path',
    servicesDesc: 'Choose a service tailored to your needs. All packages include professional offshore expertise.',
    cvUpgradeTitle: 'Offshore CV Upgrade',
    cvUpgradePrice: '€99',
    cvUpgradeDesc: 'Professional restructuring for offshore visibility.',
    cvUpgradeFeatures: ['Offshore-focused formatting', 'Detailed vessel descriptions', 'DP / sea time clarification', 'Rank progression clarity', 'Technical systems highlight', 'HR-ready PDF'],
    cvUpgradeTime: '3–5 business days',
    cvUpgradeBtn: 'Request CV Upgrade',
    docAuditTitle: 'Documents Audit',
    docAuditPrice: '€79',
    docAuditDesc: 'Certificate verification and compliance assessment.',
    docAuditFeatures: ['STCW validation', 'Offshore course verification', 'Expiry analysis', 'Renewal recommendations'],
    docAuditTime: '2–3 business days',
    docAuditBtn: 'Request Documents Audit',
    interviewPrepTitle: 'Interview Preparation (Self-Study)',
    interviewPrepPrice: '€149',
    interviewPrepDesc: 'Structured offshore interview preparation file.',
    interviewPrepFeatures: ['Technical questions by rank', 'Offshore behavioral questions', 'CV-based explanations', 'Safety & compliance positioning'],
    interviewPrepBtn: 'Request Interview Prep',
    liveInterviewTitle: 'Interview Simulation Call',
    liveInterviewPrice: '€349',
    liveInterviewDesc: '1-on-1 interview simulation (60–90 min).',
    liveInterviewFeatures: ['Technical pressure test', 'Behavioral questions', 'Live correction', 'Improvement strategy'],
    liveInterviewBtn: 'Book Interview Call',
    offshorePackageTitle: 'Offshore Ready Package',
    offshorePackagePrice: '€279',
    offshorePackageOriginal: 'Value €327 — Save €48',
    offshorePackageDesc: 'Complete preparation for serious offshore candidates.',
    offshorePackageFeatures: ['Offshore CV Upgrade', 'Documents Audit', 'Interview Preparation (Self-Study)', 'Full structured positioning before any submissions'],
    offshorePackageTime: '5–7 working days (depending on documentation volume)',
    offshorePackageBtn: 'Request Offshore Ready Package',
    targetIntroTitle: 'Target Introduction',
    targetIntroPrice: '€199',
    targetIntroDesc: 'Professional presentation of your profile to relevant offshore employers.',
    targetIntroFeatures: ['Structured submission of your CV', 'Correct offshore email positioning', 'Profile summary prepared by Elvora', 'Availability & rate alignment', 'Follow-up monitoring'],
    targetIntroNote: 'Target Introduction is available only after CV Upgrade completion.',
    targetIntroDisclaimer: 'Target Introduction does not guarantee employment. Final hiring decisions are made solely by employers.',
    targetIntroBtn: 'Request Target Introduction',
    bestValue: 'Best Value',
    mostPopular: 'Most Popular',
    forWhomBadge: "Who It's For",
    forWhomTitle: 'Is This Right for You?',
    suitableTitle: "Who It's Suitable For",
    suitableItems: ['Engineers transitioning to offshore', 'Jack-Up / DSV / DP candidates', 'Candidates targeting Middle East & Africa', 'Seafarers not getting responses', 'Rank promotion candidates'],
    notSuitableTitle: "Who It's Not For",
    notSuitableItems: ['Candidates expecting guaranteed employment', 'Free CV editing requests', 'Those not planning to improve documentation'],
    processBadge: 'Process',
    processTitle: 'How It Works',
    processDesc: 'Simple process from request to delivery.',
    steps: [{
      title: 'Submit Request',
      desc: 'Fill out the form below with your details and CV.'
    }, {
      title: 'Review & Confirm',
      desc: 'We review your profile and confirm the scope of work.'
    }, {
      title: 'Work Begins',
      desc: 'Work starts after confirmation and payment.'
    }, {
      title: 'Receive Update',
      desc: 'You receive a structured update or detailed feedback.'
    }],
    formBadge: 'Get Started',
    formTitle: 'Start Your Offshore Upgrade',
    formDesc: "Fill in your details and attach your current CV. We'll review and get back to you.",
    formSuccess: "Your request has been sent successfully. We'll contact you soon.",
    formError: 'Something went wrong. Please try again or contact us directly: ',
    labelFullName: 'Full Name',
    labelRank: 'Rank',
    rankPlaceholder: 'e.g., 3rd Engineer, DPO',
    labelNationality: 'Nationality',
    labelVesselExp: 'Vessel Experience',
    vesselExpPlaceholder: 'e.g., Jack-Up, DSV, PSV',
    labelMessenger: 'WhatsApp / Telegram',
    messengerPlaceholder: '+1 234 567 8900',
    labelEmail: 'Email',
    labelServiceType: 'Service Type',
    selectService: 'Select a service',
    serviceOptions: [{
      value: 'CV Upgrade',
      label: 'Offshore CV Upgrade — €99'
    }, {
      value: 'Documents Audit',
      label: 'Documents Audit — €79'
    }, {
      value: 'Interview Preparation',
      label: 'Interview Preparation (Self-Study) — €149'
    }, {
      value: 'Live Interview Call',
      label: 'Interview Simulation Call — €349'
    }, {
      value: 'Offshore Ready Package',
      label: 'Offshore Ready Package — €279'
    }, {
      value: 'Target Introduction',
      label: 'Target Introduction — €199'
    }],
    labelCvUpload: 'Attach CV (PDF, DOC, DOCX or images, max 48MB)',
    dragDropText: 'Drag your CV here, or click to select',
    fileTypesText: 'PDF, DOC, DOCX, PNG, JPG · Max 48 MB',
    fileSizeError: 'File size exceeds 48 MB limit',
    fileTypeError: 'Invalid file type. Please upload PDF, DOC, DOCX or image files',
    cvRequired: 'Please upload your CV',
    consentTitle: 'Consent & Agreements',
    consent1: 'I agree to the processing and storage of my personal data by Elvora Consulting for consulting and recruitment-related purposes.',
    consent2: 'I understand that Elvora Consulting provides consulting services and does not guarantee employment.',
    consent3: 'I authorize Elvora Consulting to share my CV with potential employers only after my confirmation.',
    submitBtn: 'Submit Request',
    submitting: 'Submitting...',
    consentDisclaimer: 'By submitting you agree to the processing of your personal data.',
    modalComment: 'Comment',
    modalCommentOptional: '(optional)',
    modalCommentPlaceholder: 'Any additional details...',
    modalSubmitBtn: 'Submit Request',
    modalSubmitting: 'Submitting...',
    modalSuccess: 'Request sent!',
    modalSuccessDesc: "We'll contact you soon.",
    modalError: 'Something went wrong. Please try again or contact us: ',
    modalClose: 'Close',
    labelPhone: 'Phone',
    legalTitle: 'Important Notice',
    legalText: 'Elvora Consulting provides document preparation and consulting services. We do not guarantee employment.',
    refundTitle: 'Refund Policy',
    refundText: 'Due to the nature of digital consulting services, no refunds are issued after work has begun.',
    dataTitle: 'Data Protection',
    dataText: 'All personal data is processed in accordance with EU GDPR regulations. Users may request access, correction, or deletion of their data at any time by contacting: ',
    contactBadge: 'Contact Us',
    contactTitle: 'Have Questions?',
    contactDesc: 'Contact us directly — we respond within 24 hours.',
    toastSuccess: 'Request sent successfully!',
    docTitle: 'Get Offshore Ready — ELVORA Consulting',
    metaDesc: 'Professional offshore CV upgrade, document audit, and interview preparation services. Structure your profile correctly for offshore recruiting.'
  },
  ka: {
    heroTitle1: 'თქვენი გამოცდილება პრობლემა არ არის.',
    heroTitle2: 'თქვენი პრეზენტაცია — კი.',
    heroDesc: 'ოფშორ კანდიდატების უმეტესობას უარს ეუბნებიან არა საზღვაო სტაჟის ნაკლებობის გამო — არამეტომ, რომ მათი CV გაუგებარია, არასტრუქტურირებული ან არასწორად წარმოდგენილი. Elvora გეხმარებათ სერიოზულად აღგიქვან.',
    viewServices: 'სერვისების ნახვა',
    subtitle: 'პროფესიონალური ოფშორ CV-ის, დოკუმენტებისა და გასაუბრებისთვის მომზადების სერვისები.',
    heroImgAlt: 'ოფშორ ნავთობის პლატფორმა მზის ჩასვლისას',
    problemBadge: 'პრობლემა',
    problemTitle: 'რატომ იგნორირდებიან გამოცდილი ინჟინრები',
    problemDesc: 'რეკრუტერები CV-ს 10 წამზე ნაკლებ დროში ათვალიერებენ. თუ თქვენი დოკუმენტი ამ სკანირებას ვერ გაივლის, თქვენი გამოცდილება უხილავი ხდება — მიუხედავად თქვენი კვალიფიკაციისა.',
    counterUnit: 'წმ',
    counterDesc: 'ოფშორ რეკრუტერების მიერ CV-ის განხილვის საშუალო დრო',
    ifYourDoc: 'თუ თქვენი დოკუმენტი:',
    hidesVessel: 'მალავს გემის ტიპს',
    dpHours: 'DP საათებს მკაფიოდ არ აჩვენებს',
    mixesContracts: 'კონტრაქტებს ურევს',
    noOffshoreFormat: 'არ აქვს ოფშორ ფორმატირება',
    looksGeneric: 'ზოგადად გამოიყურება',
    youAre: 'თქვენ',
    invisible: 'უხილავი ხართ',
    elvoraPromise: 'Elvora მალსახმობებს არ გპირდებათ. ჩვენ თქვენს პროფილს სწორად ვაწყობთ.',
    servicesBadge: 'სერვისები და ფასები',
    servicesTitle: 'აირჩიეთ განახლების გზა',
    servicesDesc: 'აირჩიეთ თქვენს საჭიროებებზე მორგებული სერვისი. ყველა პაკეტი მოიცავს პროფესიონალურ ოფშორ ექსპერტიზას.',
    cvUpgradeTitle: 'ოფშორ CV-ის განახლება',
    cvUpgradePrice: '€99',
    cvUpgradeDesc: 'პროფესიონალური რესტრუქტურიზაცია ოფშორ ხილვადობისთვის.',
    cvUpgradeFeatures: ['ოფშორზე ორიენტირებული ფორმატირება', 'გემების დეტალური აღწერა', 'DP / საზღვაო სტაჟის დაზუსტება', 'რანგის პროგრესიის სიცხადე', 'ტექნიკური სისტემების გამოყოფა', 'HR-ისთვის მზა PDF'],
    cvUpgradeTime: '3–5 სამუშაო დღე',
    cvUpgradeBtn: 'CV-ის განახლების მოთხოვნა',
    docAuditTitle: 'დოკუმენტების აუდიტი',
    docAuditPrice: '€79',
    docAuditDesc: 'სერტიფიკატების შემოწმება და შესაბამისობის შეფასება.',
    docAuditFeatures: ['STCW ვალიდაცია', 'ოფშორ კურსების შემოწმება', 'ვადის გასვლის ანალიზი', 'განახლების რეკომენდაციები'],
    docAuditTime: '2–3 სამუშაო დღე',
    docAuditBtn: 'დოკუმენტების აუდიტის მოთხოვნა',
    interviewPrepTitle: 'გასაუბრებისთვის მომზადება (დამოუკიდებელი)',
    interviewPrepPrice: '€149',
    interviewPrepDesc: 'სტრუქტურირებული ოფშორ გასაუბრების მოსამზადებელი ფაილი.',
    interviewPrepFeatures: ['ტექნიკური კითხვები რანგის მიხედვით', 'ოფშორ ქცევითი კითხვები', 'CV-ზე დაფუძნებული ახსნა-განმარტებები', 'უსაფრთხოებისა და შესაბამისობის პოზიციონირება'],
    interviewPrepBtn: 'გასაუბრების მომზადების მოთხოვნა',
    liveInterviewTitle: 'გასაუბრების სიმულაციის ზარი',
    liveInterviewPrice: '€349',
    liveInterviewDesc: '1-on-1 გასაუბრების სიმულაცია (60–90 წუთი).',
    liveInterviewFeatures: ['ტექნიკური ზეწოლის ტესტი', 'ქცევითი კითხვები', 'პირდაპირი კორექტირება', 'გაუმჯობესების სტრატეგია'],
    liveInterviewBtn: 'გასაუბრების ზარის დაჯავშნა',
    offshorePackageTitle: 'Offshore Ready პაკეტი',
    offshorePackagePrice: '€279',
    offshorePackageOriginal: 'ღირებულება €327 — დაზოგეთ €48',
    offshorePackageDesc: 'სრული მომზადება სერიოზული ოფშორ კანდიდატებისთვის.',
    offshorePackageFeatures: ['ოფშორ CV-ის განახლება', 'დოკუმენტების აუდიტი', 'გასაუბრებისთვის მომზადება (დამოუკიდებელი)', 'სრული სტრუქტურირებული პოზიციონირება წარდგენამდე'],
    offshorePackageTime: '5–7 სამუშაო დღე (დოკუმენტაციის მოცულობიდან გამომდინარე)',
    offshorePackageBtn: 'Offshore Ready პაკეტის მოთხოვნა',
    targetIntroTitle: 'Target Introduction',
    targetIntroPrice: '€199',
    targetIntroDesc: 'თქვენი პროფილის პროფესიონალური წარდგენა შესაბამის ოფშორ დამსაქმებლებთან.',
    targetIntroFeatures: ['თქვენი CV-ის სტრუქტურირებული წარდგენა', 'სწორი ოფშორ ელ. ფოსტის პოზიციონირება', 'Elvora-ს მიერ მომზადებული პროფილის რეზიუმე', 'ხელმისაწვდომობისა და განაკვეთის შესაბამისობა', 'შემდგომი მონიტორინგი'],
    targetIntroNote: 'Target Introduction ხელმისაწვდომია მხოლოდ CV განახლების დასრულების შემდეგ.',
    targetIntroDisclaimer: 'Target Introduction არ გარანტირებს დასაქმებას. საბოლოო დაქირავების გადაწყვეტილებებს იღებენ მხოლოდ დამსაქმებლები.',
    targetIntroBtn: 'Target Introduction-ის მოთხოვნა',
    bestValue: 'საუკეთესო ფასი',
    mostPopular: 'ყველაზე პოპულარული',
    forWhomBadge: 'ვისთვის არის',
    forWhomTitle: 'ეს თქვენთვის არის?',
    suitableTitle: 'ვისთვის არის შესაფერისი',
    suitableItems: ['ინჟინრები, რომლებიც ოფშორში გადადიან', 'Jack-Up / DSV / DP კანდიდატები', 'კანდიდატები ახლო აღმოსავლეთისა და აფრიკის მიმართულებით', 'მეზღვაურები, რომლებიც პასუხს ვერ იღებენ', 'რანგის ამაღლების კანდიდატები'],
    notSuitableTitle: 'ვისთვის არ არის შესაფერისი',
    notSuitableItems: ['კანდიდატები, რომლებიც გარანტირებულ დასაქმებას ელიან', 'უფასო CV-ის რედაქტირების მოთხოვნები', 'ისინი, ვინც დოკუმენტაციის გაუმჯობესებას არ აპირებს'],
    processBadge: 'პროცესი',
    processTitle: 'როგორ მუშაობს',
    processDesc: 'მარტივი პროცესი მოთხოვნიდან მიწოდებამდე.',
    steps: [{
      title: 'მოთხოვნის გაგზავნა',
      desc: 'შეავსეთ ქვემოთ მოცემული ფორმა თქვენი მონაცემებითა და CV-ით.'
    }, {
      title: 'შემოწმება და დადასტურება',
      desc: 'ჩვენ ვამოწმებთ თქვენს პროფილს და ვადასტურებთ სამუშაოს მოცულობას.'
    }, {
      title: 'სამუშაო იწყება',
      desc: 'სამუშაო იწყება დადასტურებისა და გადახდის შემდეგ.'
    }, {
      title: 'განახლების მიღება',
      desc: 'თქვენ იღებთ სტრუქტურირებულ განახლებას ან დეტალურ უკუკავშირს.'
    }],
    formBadge: 'დაიწყეთ',
    formTitle: 'დაიწყეთ თქვენი ოფშორ განახლება',
    formDesc: 'შეავსეთ თქვენი მონაცემები და მიამაგრეთ მიმდინარე CV. ჩვენ შევამოწმებთ და დაგიკავშირდებით.',
    formSuccess: 'თქვენი მოთხოვნა წარმატებით გაიგზავნა. მალე დაგიკავშირდებით.',
    formError: 'რაღაც შეცდომა მოხდა. გთხოვთ, სცადეთ ხელახლა ან დაგვიკავშირდით პირდაპირ: ',
    labelFullName: 'სრული სახელი',
    labelRank: 'რანგი',
    rankPlaceholder: 'მაგ., მე-3 ინჟინერი, DPO',
    labelNationality: 'მოქალაქეობა',
    labelVesselExp: 'გემის გამოცდილება',
    vesselExpPlaceholder: 'მაგ., Jack-Up, DSV, PSV',
    labelMessenger: 'WhatsApp / Telegram',
    messengerPlaceholder: '+1 234 567 8900',
    labelEmail: 'ელ. ფოსტა',
    labelServiceType: 'სერვისის ტიპი',
    selectService: 'აირჩიეთ სერვისი',
    serviceOptions: [{
      value: 'CV Upgrade',
      label: 'ოფშორ CV-ის განახლება — €99'
    }, {
      value: 'Documents Audit',
      label: 'დოკუმენტების აუდიტი — €79'
    }, {
      value: 'Interview Preparation',
      label: 'გასაუბრებისთვის მომზადება (დამოუკიდებელი) — €149'
    }, {
      value: 'Live Interview Call',
      label: 'გასაუბრების სიმულაციის ზარი — €349'
    }, {
      value: 'Offshore Ready Package',
      label: 'Offshore Ready პაკეტი — €279'
    }, {
      value: 'Target Introduction',
      label: 'Target Introduction — €199'
    }],
    labelCvUpload: 'CV-ის მიმაგრება (PDF, DOC, DOCX ან სურათები, მაქს. 48MB)',
    dragDropText: 'გადმოიტანეთ თქვენი CV აქ, ან დააწკაპუნეთ ასარჩევად',
    fileTypesText: 'PDF, DOC, DOCX, PNG, JPG · მაქს. 48 MB',
    fileSizeError: 'ფაილის ზომა აღემატება 48 MB-ის ლიმიტს',
    fileTypeError: 'არასწორი ფაილის ტიპი. გთხოვთ, ატვირთეთ PDF, DOC, DOCX ან სურათის ფაილები',
    cvRequired: 'გთხოვთ, ატვირთეთ თქვენი CV',
    consentTitle: 'თანხმობა და შეთანხმებები',
    consent1: 'ვეთანხმები ჩემი პერსონალური მონაცემების დამუშავებასა და შენახვას Elvora Consulting-ის მიერ საკონსულტაციო და რეკრუტინგთან დაკავშირებული მიზნებისთვის.',
    consent2: 'მესმის, რომ Elvora Consulting საკონსულტაციო მომსახურებას უწევს და დასაქმებას არ გარანტირებს.',
    consent3: 'ვანიჭებ Elvora Consulting-ს უფლებას, გაუზიაროს ჩემი CV პოტენციურ დამსაქმებლებს მხოლოდ ჩემი დადასტურების შემდეგ.',
    submitBtn: 'მოთხოვნის გაგზავნა',
    submitting: 'იგზავნება...',
    consentDisclaimer: 'გაგზავნით თქვენ ეთანხმებით თქვენი პერსონალური მონაცემების დამუშავებას.',
    modalComment: 'კომენტარი',
    modalCommentOptional: '(არასავალდებულო)',
    modalCommentPlaceholder: 'ნებისმიერი დამატებითი დეტალი...',
    modalSubmitBtn: 'მოთხოვნის გაგზავნა',
    modalSubmitting: 'იგზავნება...',
    modalSuccess: 'მოთხოვნა გაიგზავნა!',
    modalSuccessDesc: 'მალე დაგიკავშირდებით.',
    modalError: 'რაღაც შეცდომა მოხდა. გთხოვთ, სცადეთ ხელახლა ან დაგვიკავშირდით: ',
    modalClose: 'დახურვა',
    labelPhone: 'ტელეფონი',
    legalTitle: 'მნიშვნელოვანი შენიშვნა',
    legalText: 'Elvora Consulting უზრუნველყოფს დოკუმენტების მომზადებისა და საკონსულტაციო მომსახურებას. ჩვენ არ ვგარანტირებთ დასაქმებას.',
    refundTitle: 'თანხის დაბრუნების პოლიტიკა',
    refundText: 'ციფრული საკონსულტაციო მომსახურების ხასიათიდან გამომდინარე, სამუშაოს დაწყების შემდეგ თანხა არ ბრუნდება.',
    dataTitle: 'მონაცემთა დაცვა',
    dataText: 'ყველა პერსონალური მონაცემი მუშავდება EU GDPR რეგულაციების შესაბამისად. მომხმარებლებს შეუძლიათ ნებისმიერ დროს მოითხოვონ მონაცემებზე წვდომა, კორექტირება ან წაშლა, დაკავშირებით: ',
    contactBadge: 'დაგვიკავშირდით',
    contactTitle: 'გაქვთ კითხვები?',
    contactDesc: 'დაგვიკავშირდით პირდაპირ — ვპასუხობთ 24 საათში.',
    toastSuccess: 'მოთხოვნა წარმატებით გაიგზავნა!',
    docTitle: 'გახდი ოფშორისთვის მზად — ELVORA Consulting',
    metaDesc: 'პროფესიონალური ოფშორ CV-ის განახლება, დოკუმენტების აუდიტი და გასაუბრებისთვის მომზადების სერვისები. სწორად მოაწყვეთ თქვენი პროფილი ოფშორ რეკრუტინგისთვის.'
  }
} as const;
type ContentLang = keyof typeof pageContent;
const MAX_FILE_SIZE = 48 * 1024 * 1024;
const selectStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat' as const,
  backgroundPosition: 'right 12px center'
};
const ALLOWED_FILE_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
/* ── Check if mobile for lighter animations ── */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}
/* ── Scroll reveal with varied animation directions ── */
type RevealDirection = 'up' | 'left' | 'right' | 'fade' | 'scale';
function Reveal({
  children,
  className = '',
  delay = 0,
  direction = 'up'
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: RevealDirection;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const isMobile = useIsMobile();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.unobserve(el);
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const mobileDelay = isMobile ? 0 : delay;
  const duration = isMobile ? 0.4 : 0.65;
  const hiddenTransform: Record<RevealDirection, string> = {
    up: `translateY(${isMobile ? 12 : 20}px)`,
    left: 'translateX(-20px)',
    right: 'translateX(20px)',
    fade: 'none',
    scale: 'scale(0.96)'
  };
  return <div ref={ref} className={className} style={{
    opacity: visible ? 1 : 0,
    transform: visible ? 'none' : hiddenTransform[direction],
    transition: `opacity ${duration}s cubic-bezier(0.16,1,0.3,1) ${mobileDelay}ms, transform ${duration}s cubic-bezier(0.16,1,0.3,1) ${mobileDelay}ms`,
    willChange: visible ? 'auto' : 'opacity, transform'
  }} data-id="element-849">
      {children}
    </div>;
}
/* ── Smooth animated counter with easing ── */
function AnimatedCounter({
  lang
}: {
  lang: ContentLang;
}) {
  const c = pageContent[lang] || pageContent.en;
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        const duration = 1200;
        const end = 10;
        let startTime: number | null = null;
        function easeOutQuart(t: number) {
          return 1 - Math.pow(1 - t, 4);
        }
        function animate(timestamp: number) {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          setCount(Math.round(easeOutQuart(progress) * end));
          if (progress < 1) requestAnimationFrame(animate);
        }
        requestAnimationFrame(animate);
      }
    }, {
      threshold: 0.3
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className="flex items-center gap-2.5" data-id="element-850">
      <div className="w-10 h-10 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-851">
        <ClockIcon className="w-5 h-5 text-[var(--color-tan)]" data-id="element-852" />
      </div>
      <div data-id="element-853">
        <div className="flex items-baseline gap-1" data-id="element-854">
          <span className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] tabular-nums" data-id="element-855">
            {count}
          </span>
          <span className="text-lg md:text-xl font-bold text-[var(--color-text-light)]" data-id="element-856">
            {c.counterUnit}
          </span>
        </div>
        <p className="text-xs text-[var(--color-text-light)] mt-0.5" data-id="element-857">
          {c.counterDesc}
        </p>
      </div>
    </div>;
}
/* ── Fixed success toast ── */
function SuccessToast({
  show,
  lang
}: {
  show: boolean;
  lang: ContentLang;
}) {
  const c = pageContent[lang] || pageContent.en;
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [show]);
  if (!show) return null;
  return ReactDOM.createPortal(<div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none" style={{
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(16px)',
    transition: 'opacity 0.4s cubic-bezier(0.16,1,0.3,1), transform 0.4s cubic-bezier(0.16,1,0.3,1)'
  }} data-id="element-858">
      <div className="flex items-center gap-3 px-6 py-4 bg-green-600 text-white rounded-xl shadow-2xl pointer-events-auto" data-id="element-859">
        <CheckCircle className="w-5 h-5 flex-shrink-0" data-id="element-860" />
        <span className="text-sm font-medium" data-id="element-861">{c.toastSuccess}</span>
      </div>
    </div>, document.body);
}
/* ══════════════════════════════════════════════════
   SERVICE REQUEST MODAL (popup for each service)
   ══════════════════════════════════════════════════ */
function ServiceRequestModal({
  isOpen,
  onClose,
  serviceName,
  servicePrice,
  lang
}: {
  isOpen: boolean;
  onClose: () => void;
  serviceName: string;
  servicePrice: string;
  lang: ContentLang;
}) {
  const c = pageContent[lang] || pageContent.en;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  const resetForm = () => {
    setName('');
    setPhone('');
    setEmail('');
    setComment('');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setStatus('idle');
    const fd = new FormData();
    fd.append('name', name);
    fd.append('email', email);
    fd.append('phone', phone);
    fd.append('message', `[Offshore Service: ${serviceName} — ${servicePrice}]${comment ? ` Comment: ${comment}` : ''}`);
    try {
      const result = await submitForm('home', fd, lang);
      if (result.ok) {
        setStatus('success');
        resetForm();
        setTimeout(() => {
          setStatus('idle');
          onClose();
        }, 2500);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return ReactDOM.createPortal(<div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-862">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl my-8" role="dialog" aria-modal="true" data-id="element-863">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between" data-id="element-864">
          <div data-id="element-865">
            <h2 className="text-lg font-bold text-[var(--color-text-dark)]" data-id="element-866">
              {serviceName}
            </h2>
            <p className="text-sm text-[var(--color-tan)] font-semibold" data-id="element-867">
              {servicePrice}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label={c.modalClose} data-id="element-868">
            <X className="w-5 h-5 text-gray-500" data-id="element-869" />
          </button>
        </div>

        {/* Success overlay */}
        {status === 'success' && <div className="absolute inset-0 z-20 bg-white/95 rounded-2xl flex flex-col items-center justify-center gap-3" data-id="element-870">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center" data-id="element-871">
              <CheckCircle className="w-8 h-8 text-green-600" data-id="element-872" />
            </div>
            <p className="text-lg font-semibold text-[var(--color-text-dark)]" data-id="element-873">
              {c.modalSuccess}
            </p>
            <p className="text-sm text-[var(--color-text-light)]" data-id="element-874">
              {c.modalSuccessDesc}
            </p>
          </div>}

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-875">
          <div data-id="element-876">
            <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-877">
              {c.labelFullName} <span className="text-red-500" data-id="element-878">*</span>
            </label>
            <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-879" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-880">
            <div data-id="element-881">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-882">
                {c.labelPhone} <span className="text-red-500" data-id="element-883">*</span>
              </label>
              <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors placeholder:text-gray-400" data-id="element-884" />
            </div>
            <div data-id="element-885">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-886">
                {c.labelEmail} <span className="text-red-500" data-id="element-887">*</span>
              </label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-888" />
            </div>
          </div>
          <div data-id="element-889">
            <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-890">
              {c.modalComment}{' '}
              <span className="text-[var(--color-text-light)]" data-id="element-891">
                {c.modalCommentOptional}
              </span>
            </label>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} placeholder={c.modalCommentPlaceholder} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors resize-none placeholder:text-gray-400" data-id="element-892" />
          </div>

          {status === 'error' && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-893">
              <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-894" />
              <span data-id="element-895">
                {c.modalError}
                <a href="mailto:info@elvora.consulting" className="underline font-medium" data-id="element-896">
                  info@elvora.consulting
                </a>
              </span>
            </div>}

          <button type="submit" disabled={submitting} className={`w-full px-6 py-3.5 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${!submitting ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-897">
            {submitting ? <>
                <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-898" />{' '}
                {c.modalSubmitting}
              </> : <>
                <ArrowRight className="w-4 h-4" data-id="element-899" /> {c.modalSubmitBtn}
              </>}
          </button>

          <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-900">
            {c.consentDisclaimer}
          </p>
        </form>
      </div>
    </div>, document.body);
}
/* ══════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ══════════════════════════════════════════════════ */
export function OffshoreReadyPage() {
  const {
    language
  } = useLanguage();
  const lang: ContentLang = language === 'ka' ? 'ka' : 'en';
  const c = pageContent[lang];
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalService, setModalService] = useState({
    name: '',
    price: ''
  });
  const openServiceModal = (name: string, price: string) => {
    setModalService({
      name,
      price
    });
    setModalOpen(true);
  };
  useEffect(() => {
    document.title = c.docTitle;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', c.metaDesc);
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + window.location.pathname;
    // Open Graph tags
    const ogTags: Record<string, string> = {
      'og:title': c.docTitle,
      'og:description': c.metaDesc,
      'og:type': 'website',
      'og:url': window.location.origin + window.location.pathname,
      'og:image': 'https://cdn.magicpatterns.com/uploads/pqSn4sKjj7UaiJA9efsEDm/mathias-reding-NtgkhdZz-Y4-unsplash.jpg',
      'og:site_name': 'ELVORA Consulting'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
    // Twitter Card tags
    const twitterTags: Record<string, string> = {
      'twitter:card': 'summary_large_image',
      'twitter:title': c.docTitle,
      'twitter:description': c.metaDesc,
      'twitter:image': 'https://cdn.magicpatterns.com/uploads/pqSn4sKjj7UaiJA9efsEDm/mathias-reding-NtgkhdZz-Y4-unsplash.jpg'
    };
    Object.entries(twitterTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
  }, [lang, c.docTitle, c.metaDesc]);
  const resetForm = () => {
    setFullName('');
    setPhone('');
    setEmail('');
    setServiceType('');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitStatus('idle');
    const fd = new FormData();
    fd.append('name', fullName);
    fd.append('email', email);
    fd.append('phone', phone);
    fd.append('message', `[Offshore Service Request] Service: ${serviceType}`);
    try {
      const result = await submitForm('home', fd, lang);
      if (result.ok) {
        setSubmitStatus('success');
        resetForm();
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };
  const scrollToServices = () => {
    document.getElementById('offshore-services')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const suitableIcons = [Anchor, Wrench, Globe, MailX, TrendingUp];
  const notSuitableIcons = [ShieldOff, Ban, FileX];
  const stepIcons = [Send, Search, UserCheck, Handshake];
  const docIssueIcons = [Ship, ClockIcon, Shuffle, FileX, Copy];
  const docIssueLabels = [c.hidesVessel, c.dpHours, c.mixesContracts, c.noOffshoreFormat, c.looksGeneric];
  return <div className="min-h-screen bg-white" data-id="element-901">
      <Header data-id="element-902" />

      <ServiceRequestModal isOpen={modalOpen} onClose={() => setModalOpen(false)} serviceName={modalService.name} servicePrice={modalService.price} lang={lang} data-id="element-903" />

      <SuccessToast show={submitStatus === 'success'} lang={lang} data-id="element-904" />

      {/* HERO SECTION */}
      <section className="w-full px-6 md:px-12 py-8" data-id="element-905">
        <div className="max-w-7xl mx-auto" data-id="element-906">
          {/* Desktop */}
          <div className="hidden md:block relative w-full rounded-2xl overflow-hidden" data-id="element-907">
            <img src="https://cdn.magicpatterns.com/uploads/pqSn4sKjj7UaiJA9efsEDm/mathias-reding-NtgkhdZz-Y4-unsplash.jpg" alt={c.heroImgAlt} className="w-full h-[600px] lg:h-[700px] object-cover object-center" fetchPriority="high" decoding="async" width={1400} height={700} sizes="(min-width: 1280px) 1200px, 100vw" data-id="element-908" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-909" />
            <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12 pb-20 lg:pb-24" data-id="element-910">
              <div className="max-w-2xl" data-id="element-911">
                <h1 className="text-3xl lg:text-5xl font-bold text-white mb-1 leading-tight drop-shadow-lg" data-id="element-912">
                  {c.heroTitle1}
                </h1>
                <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 leading-tight drop-shadow-lg" data-id="element-913">
                  {c.heroTitle2}
                </h1>
                <p className="text-white/95 text-base lg:text-lg leading-relaxed mb-6 drop-shadow-md" data-id="element-914">
                  {c.heroDesc}
                </p>
                <button onClick={scrollToServices} className="px-6 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity shadow-lg" data-id="element-915">
                  {c.viewServices}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden space-y-6 pb-20" data-id="element-916">
            <div className="text-center" data-id="element-917">
              <h1 className="text-3xl font-bold text-[var(--color-dark)] leading-tight" data-id="element-918">
                {c.heroTitle1}
              </h1>
              <h1 className="text-3xl font-bold text-[var(--color-dark)] leading-tight mt-1" data-id="element-919">
                {c.heroTitle2}
              </h1>
            </div>
            <div className="relative w-full rounded-2xl overflow-hidden" data-id="element-920">
              <img src="https://cdn.magicpatterns.com/uploads/pqSn4sKjj7UaiJA9efsEDm/mathias-reding-NtgkhdZz-Y4-unsplash.jpg" alt={c.heroImgAlt} className="w-full h-[300px] object-cover" fetchPriority="high" decoding="async" width={800} height={300} sizes="100vw" style={{
              objectPosition: '50% 50%'
            }} data-id="element-921" />
            </div>
            <p className="text-[var(--color-dark)]/80 text-sm leading-relaxed text-center" data-id="element-922">
              {c.heroDesc}
            </p>
            <button onClick={scrollToServices} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity" data-id="element-923">
              {c.viewServices}
            </button>
          </div>

          <p className="text-center text-[var(--color-text-light)] text-sm mt-6" data-id="element-924">
            {c.subtitle}
          </p>
        </div>
      </section>

      {/* WHY STRONG ENGINEERS GET IGNORED */}
      <div className="cv-lazy-section" data-id="element-925">
        <section className="w-full px-6 md:px-12 py-16 md:py-20 bg-white" data-id="element-926">
          <div className="max-w-7xl mx-auto" data-id="element-927">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-stretch" data-id="element-928">
              <div className="lg:w-2/5 flex flex-col" data-id="element-929">
                <Reveal direction="left" data-id="element-930">
                  <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6 bg-white w-fit" data-id="element-931">
                    <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-932">
                      {c.problemBadge}
                    </span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-6 leading-tight" data-id="element-933">
                    {c.problemTitle}
                  </h2>
                  <p className="text-[var(--color-text-light)] text-sm leading-relaxed mb-6" data-id="element-934">
                    {c.problemDesc}
                  </p>
                </Reveal>
                <Reveal className="mt-auto" delay={200} direction="fade" data-id="element-935">
                  <div className="bg-white rounded-xl p-5" data-id="element-936">
                    <AnimatedCounter lang={lang} data-id="element-937" />
                  </div>
                </Reveal>
              </div>

              <div className="lg:w-3/5 flex flex-col" data-id="element-938">
                <Reveal direction="right" data-id="element-939">
                  <p className="text-sm font-medium text-[var(--color-text-dark)] mb-5" data-id="element-940">
                    {c.ifYourDoc}
                  </p>
                </Reveal>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1" data-id="element-941">
                  {docIssueLabels.map((label, i) => {
                  const Icon = docIssueIcons[i];
                  return <Reveal key={label} delay={80 * (i + 1)} direction="scale" data-id="element-942">
                        <div className="bg-white border border-gray-100 rounded-xl p-5 h-full" data-id="element-943">
                          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center mb-3" data-id="element-944">
                            <Icon className="w-5 h-5 text-red-400" data-id="element-945" />
                          </div>
                          <p className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-946">
                            {label}
                          </p>
                        </div>
                      </Reveal>;
                })}
                </div>
              </div>
            </div>

            <Reveal delay={100} direction="fade" data-id="element-947">
              <div className="bg-gray-50/70 border border-gray-200 rounded-2xl p-6 md:p-8 mt-10" data-id="element-948">
                <div className="flex items-center gap-4 sm:gap-5 justify-center" data-id="element-949">
                  <div className="w-12 h-12 rounded-full bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-950">
                    <AlertCircle className="w-6 h-6 text-[var(--color-tan)]" data-id="element-951" />
                  </div>
                  <div data-id="element-952">
                    <p className="text-xl font-bold text-[var(--color-text-dark)]" data-id="element-953">
                      {c.youAre}{' '}
                      <span className="text-[var(--color-tan)]" data-id="element-954">
                        {c.invisible}
                      </span>
                      .
                    </p>
                    <p className="text-sm text-[var(--color-text-light)] mt-0.5" data-id="element-955">
                      {c.elvoraPromise}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>

      {/* SERVICES BLOCK */}
      <div className="cv-lazy-section" data-id="element-956">
        <section id="offshore-services" className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-957">
          <div className="max-w-7xl mx-auto" data-id="element-958">
            <Reveal direction="fade" data-id="element-959">
              <div className="text-center mb-12" data-id="element-960">
                <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6 bg-white" data-id="element-961">
                  <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-962">
                    {c.servicesBadge}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-963">
                  {c.servicesTitle}
                </h2>
                <p className="text-[var(--color-text-light)] text-base max-w-2xl mx-auto" data-id="element-964">
                  {c.servicesDesc}
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-965">
              {/* CV Upgrade */}
              <Reveal delay={0} direction="left" data-id="element-966">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-967">
                  <div className="p-6 md:p-8 flex flex-col flex-1" data-id="element-968">
                    <div className="flex items-start justify-between mb-4" data-id="element-969">
                      <div className="w-12 h-12 rounded-xl bg-[var(--color-bg)] flex items-center justify-center" data-id="element-970">
                        <FileText className="w-6 h-6 text-[var(--color-tan)]" data-id="element-971" />
                      </div>
                      <span className="text-3xl font-bold text-[var(--color-tan)]" data-id="element-972">
                        {c.cvUpgradePrice}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[var(--color-text-dark)] mb-2" data-id="element-973">
                      {c.cvUpgradeTitle}
                    </h3>
                    <p className="text-sm text-[var(--color-text-light)] mb-5" data-id="element-974">
                      {c.cvUpgradeDesc}
                    </p>
                    <div className="space-y-2.5 mb-6 flex-1" data-id="element-975">
                      {c.cvUpgradeFeatures.map(item => <div key={item} className="flex items-start gap-2.5" data-id="element-976">
                          <CheckCircle className="w-4 h-4 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-977" />
                          <span className="text-sm text-[var(--color-text-dark)]" data-id="element-978">
                            {item}
                          </span>
                        </div>)}
                    </div>
                    <div className="mt-auto" data-id="element-979">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-[var(--color-text-light)] mb-4 w-fit" data-id="element-980">
                        <ClockIcon className="w-3.5 h-3.5" data-id="element-981" />
                        {c.cvUpgradeTime}
                      </div>
                      <button onClick={() => openServiceModal(c.cvUpgradeTitle, c.cvUpgradePrice)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-982">
                        {c.cvUpgradeBtn}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* Documents Audit */}
              <Reveal delay={80} direction="right" data-id="element-983">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-984">
                  <div className="p-6 md:p-8 flex flex-col flex-1" data-id="element-985">
                    <div className="flex items-start justify-between mb-4" data-id="element-986">
                      <div className="w-12 h-12 rounded-xl bg-[var(--color-bg)] flex items-center justify-center" data-id="element-987">
                        <ClipboardCheck className="w-6 h-6 text-[var(--color-tan)]" data-id="element-988" />
                      </div>
                      <span className="text-3xl font-bold text-[var(--color-tan)]" data-id="element-989">
                        {c.docAuditPrice}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[var(--color-text-dark)] mb-2" data-id="element-990">
                      {c.docAuditTitle}
                    </h3>
                    <p className="text-sm text-[var(--color-text-light)] mb-5" data-id="element-991">
                      {c.docAuditDesc}
                    </p>
                    <div className="space-y-2.5 mb-6 flex-1" data-id="element-992">
                      {c.docAuditFeatures.map(item => <div key={item} className="flex items-start gap-2.5" data-id="element-993">
                          <CheckCircle className="w-4 h-4 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-994" />
                          <span className="text-sm text-[var(--color-text-dark)]" data-id="element-995">
                            {item}
                          </span>
                        </div>)}
                    </div>
                    <div className="mt-auto" data-id="element-996">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-[var(--color-text-light)] mb-4 w-fit" data-id="element-997">
                        <ClockIcon className="w-3.5 h-3.5" data-id="element-998" />
                        {c.docAuditTime}
                      </div>
                      <button onClick={() => openServiceModal(c.docAuditTitle, c.docAuditPrice)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-999">
                        {c.docAuditBtn}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* Interview Preparation */}
              <Reveal delay={160} direction="left" data-id="element-1000">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-1001">
                  <div className="p-6 md:p-8 flex flex-col flex-1" data-id="element-1002">
                    <div className="flex items-start justify-between mb-4" data-id="element-1003">
                      <div className="w-12 h-12 rounded-xl bg-[var(--color-bg)] flex items-center justify-center" data-id="element-1004">
                        <BookOpen className="w-6 h-6 text-[var(--color-tan)]" data-id="element-1005" />
                      </div>
                      <span className="text-3xl font-bold text-[var(--color-tan)]" data-id="element-1006">
                        {c.interviewPrepPrice}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[var(--color-text-dark)] mb-2" data-id="element-1007">
                      {c.interviewPrepTitle}
                    </h3>
                    <p className="text-sm text-[var(--color-text-light)] mb-5" data-id="element-1008">
                      {c.interviewPrepDesc}
                    </p>
                    <div className="space-y-2.5 mb-6 flex-1" data-id="element-1009">
                      {c.interviewPrepFeatures.map(item => <div key={item} className="flex items-start gap-2.5" data-id="element-1010">
                          <CheckCircle className="w-4 h-4 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-1011" />
                          <span className="text-sm text-[var(--color-text-dark)]" data-id="element-1012">
                            {item}
                          </span>
                        </div>)}
                    </div>
                    <div className="mt-auto" data-id="element-1013">
                      <button onClick={() => openServiceModal(c.interviewPrepTitle, c.interviewPrepPrice)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-1014">
                        {c.interviewPrepBtn}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* Live Interview Simulation — Most Popular */}
              <Reveal delay={240} direction="right" data-id="element-1015">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-1016">
                  <div className="px-6 pt-4 md:px-8 md:pt-5" data-id="element-1017">
                    <span className="inline-block px-3 py-1 bg-[var(--color-tan)] text-white text-xs font-semibold rounded-full" data-id="element-1018">
                      {c.mostPopular}
                    </span>
                  </div>
                  <div className="p-6 md:p-8 pt-3 md:pt-3 flex flex-col flex-1" data-id="element-1019">
                    <div className="flex items-start justify-between mb-4" data-id="element-1020">
                      <div className="w-12 h-12 rounded-xl bg-[var(--color-bg)] flex items-center justify-center" data-id="element-1021">
                        <Video className="w-6 h-6 text-[var(--color-tan)]" data-id="element-1022" />
                      </div>
                      <span className="text-3xl font-bold text-[var(--color-tan)]" data-id="element-1023">
                        {c.liveInterviewPrice}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[var(--color-text-dark)] mb-2" data-id="element-1024">
                      {c.liveInterviewTitle}
                    </h3>
                    <p className="text-sm text-[var(--color-text-light)] mb-5" data-id="element-1025">
                      {c.liveInterviewDesc}
                    </p>
                    <div className="space-y-2.5 mb-6 flex-1" data-id="element-1026">
                      {c.liveInterviewFeatures.map(item => <div key={item} className="flex items-start gap-2.5" data-id="element-1027">
                          <CheckCircle className="w-4 h-4 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-1028" />
                          <span className="text-sm text-[var(--color-text-dark)]" data-id="element-1029">
                            {item}
                          </span>
                        </div>)}
                    </div>
                    <div className="mt-auto" data-id="element-1030">
                      <button onClick={() => openServiceModal(c.liveInterviewTitle, c.liveInterviewPrice)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-1031">
                        {c.liveInterviewBtn}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* Offshore Ready Package — Best Value */}
              <Reveal delay={320} direction="left" data-id="element-1032">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-1033">
                  <div className="p-6 md:p-8 flex flex-col flex-1" data-id="element-1034">
                    <div className="flex items-start justify-between mb-4" data-id="element-1035">
                      <div className="w-12 h-12 rounded-xl bg-[var(--color-bg)] flex items-center justify-center" data-id="element-1036">
                        <CheckCircle className="w-6 h-6 text-[var(--color-tan)]" data-id="element-1037" />
                      </div>
                      <div className="text-right" data-id="element-1038">
                        <span className="text-3xl font-bold text-[var(--color-tan)] block" data-id="element-1039">
                          {c.offshorePackagePrice}
                        </span>
                        <span className="text-xs text-green-600 font-medium" data-id="element-1040">
                          {c.offshorePackageOriginal}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-[var(--color-text-dark)] mb-2" data-id="element-1041">
                      {c.offshorePackageTitle}
                    </h3>
                    <p className="text-sm text-[var(--color-text-light)] mb-5" data-id="element-1042">
                      {c.offshorePackageDesc}
                    </p>
                    <div className="space-y-2.5 mb-6 flex-1" data-id="element-1043">
                      {c.offshorePackageFeatures.map(item => <div key={item} className="flex items-start gap-2.5" data-id="element-1044">
                          <CheckCircle className="w-4 h-4 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-1045" />
                          <span className="text-sm text-[var(--color-text-dark)]" data-id="element-1046">
                            {item}
                          </span>
                        </div>)}
                    </div>
                    <div className="mt-auto" data-id="element-1047">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-[var(--color-text-light)] mb-4 w-fit" data-id="element-1048">
                        <ClockIcon className="w-3.5 h-3.5" data-id="element-1049" />
                        {c.offshorePackageTime}
                      </div>
                      <button onClick={() => openServiceModal(c.offshorePackageTitle, c.offshorePackagePrice)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-1050">
                        {c.offshorePackageBtn}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* Target Introduction */}
              <Reveal delay={400} direction="right" data-id="element-1051">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-1052">
                  <div className="p-6 md:p-8 flex flex-col flex-1" data-id="element-1053">
                    <div className="flex items-start justify-between mb-4" data-id="element-1054">
                      <div className="w-12 h-12 rounded-xl bg-[var(--color-bg)] flex items-center justify-center" data-id="element-1055">
                        <Send className="w-6 h-6 text-[var(--color-tan)]" data-id="element-1056" />
                      </div>
                      <span className="text-3xl font-bold text-[var(--color-tan)]" data-id="element-1057">
                        {c.targetIntroPrice}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[var(--color-text-dark)] mb-2" data-id="element-1058">
                      {c.targetIntroTitle}
                    </h3>
                    <p className="text-sm text-[var(--color-text-light)] mb-5" data-id="element-1059">
                      {c.targetIntroDesc}
                    </p>
                    <div className="space-y-2.5 mb-4 flex-1" data-id="element-1060">
                      {c.targetIntroFeatures.map(item => <div key={item} className="flex items-start gap-2.5" data-id="element-1061">
                          <CheckCircle className="w-4 h-4 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-1062" />
                          <span className="text-sm text-[var(--color-text-dark)]" data-id="element-1063">
                            {item}
                          </span>
                        </div>)}
                    </div>
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg" data-id="element-1064">
                      <p className="text-xs text-blue-800 font-medium" data-id="element-1065">
                        {c.targetIntroNote}
                      </p>
                    </div>
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-1066">
                      <p className="text-xs text-amber-900" data-id="element-1067">
                        {c.targetIntroDisclaimer}
                      </p>
                    </div>
                    <div className="mt-auto" data-id="element-1068">
                      <button onClick={() => openServiceModal(c.targetIntroTitle, c.targetIntroPrice)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-1069">
                        {c.targetIntroBtn}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      </div>

      {/* BOOKS SECTION */}
      <div className="cv-lazy-section" data-id="element-1070">
        <BooksSection variant="full" data-id="element-1071" />
      </div>

      {/* IS THIS RIGHT FOR YOU? */}
      <div className="cv-lazy-section-sm" data-id="element-1072">
        <section className="w-full px-6 md:px-12 py-16 md:py-20 bg-white" data-id="element-1073">
          <div className="max-w-7xl mx-auto" data-id="element-1074">
            <Reveal direction="fade" data-id="element-1075">
              <div className="text-center mb-12" data-id="element-1076">
                <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6 bg-white" data-id="element-1077">
                  <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1078">
                    {c.forWhomBadge}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)]" data-id="element-1079">
                  {c.forWhomTitle}
                </h2>
              </div>
            </Reveal>

            <Reveal delay={100} direction="up" data-id="element-1080">
              <div className="mb-10 bg-green-50/50 border border-green-100 rounded-2xl p-6 md:p-8" data-id="element-1081">
                <div className="flex items-center gap-3 mb-6" data-id="element-1082">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-1083">
                    <CheckCircle className="w-5 h-5 text-green-600" data-id="element-1084" />
                  </div>
                  <div data-id="element-1085">
                    <h3 className="text-lg font-bold text-[var(--color-text-dark)]" data-id="element-1086">
                      {c.suitableTitle}
                    </h3>
                    <div className="h-0.5 w-12 bg-green-500 rounded-full mt-1" data-id="element-1087" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1088">
                  {c.suitableItems.map((label, i) => {
                  const Icon = suitableIcons[i];
                  return <Reveal key={label} delay={60 * (i + 1)} direction="scale" data-id="element-1089">
                        <div className="bg-white border border-green-100 rounded-xl p-5 h-full" data-id="element-1090">
                          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center mb-3" data-id="element-1091">
                            <Icon className="w-5 h-5 text-green-600" data-id="element-1092" />
                          </div>
                          <p className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1093">
                            {label}
                          </p>
                        </div>
                      </Reveal>;
                })}
                </div>
              </div>
            </Reveal>

            <Reveal delay={200} direction="up" data-id="element-1094">
              <div className="bg-red-50/50 border border-red-100 rounded-2xl p-6 md:p-8" data-id="element-1095">
                <div className="flex items-center gap-3 mb-6" data-id="element-1096">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-1097">
                    <XCircle className="w-5 h-5 text-red-500" data-id="element-1098" />
                  </div>
                  <div data-id="element-1099">
                    <h3 className="text-lg font-bold text-[var(--color-text-dark)]" data-id="element-1100">
                      {c.notSuitableTitle}
                    </h3>
                    <div className="h-0.5 w-12 bg-red-500 rounded-full mt-1" data-id="element-1101" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1102">
                  {c.notSuitableItems.map((label, i) => {
                  const Icon = notSuitableIcons[i];
                  return <Reveal key={label} delay={60 * (i + 1)} direction="scale" data-id="element-1103">
                        <div className="bg-white border border-red-100 rounded-xl p-5 h-full" data-id="element-1104">
                          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center mb-3" data-id="element-1105">
                            <Icon className="w-5 h-5 text-red-500" data-id="element-1106" />
                          </div>
                          <p className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1107">
                            {label}
                          </p>
                        </div>
                      </Reveal>;
                })}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>

      {/* HOW IT WORKS */}
      <div className="cv-lazy-section" data-id="element-1108">
        <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-1109">
          <div className="max-w-5xl mx-auto" data-id="element-1110">
            <Reveal direction="fade" data-id="element-1111">
              <div className="text-center mb-10" data-id="element-1112">
                <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-5 bg-white" data-id="element-1113">
                  <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1114">
                    {c.processBadge}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-3" data-id="element-1115">
                  {c.processTitle}
                </h2>
                <p className="text-base text-gray-600 max-w-xl mx-auto" data-id="element-1116">
                  {c.processDesc}
                </p>
              </div>
            </Reveal>

            {/* Desktop Zigzag */}
            <div className="hidden md:block relative" data-id="element-1117">
              <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-gray-200" data-id="element-1118" />
              <div className="space-y-5 relative" data-id="element-1119">
                {c.steps.map((step, i) => {
                const StepIcon = stepIcons[i];
                const num = String(i + 1).padStart(2, '0');
                const right = i % 2 === 0;
                return <Reveal key={num} delay={120 * i} direction={right ? 'right' : 'left'} data-id="element-1120">
                      <div className="relative" data-id="element-1121">
                        <div className={`absolute top-1/2 -translate-y-1/2 h-px bg-gray-200 ${right ? 'left-1/2 w-[8%]' : 'right-1/2 w-[8%]'}`} data-id="element-1122" />
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[var(--color-tan)] z-10" data-id="element-1123" />
                        <div className={`flex ${right ? 'justify-end' : 'justify-start'}`} data-id="element-1124">
                          <div className="w-[42%]" data-id="element-1125">
                            <div className="bg-white border border-gray-200 rounded-xl p-5 relative" data-id="element-1126">
                              <span className="absolute top-3 right-4 text-xs font-medium text-gray-400" data-id="element-1127">
                                {num}
                              </span>
                              <div className="flex items-center gap-4 pr-10" data-id="element-1128">
                                <div className="w-10 h-10 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-1129">
                                  <StepIcon className="w-5 h-5 text-[var(--color-tan)]" data-id="element-1130" />
                                </div>
                                <div className="flex-1" data-id="element-1131">
                                  <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-1132">
                                    {step.title}
                                  </h3>
                                  <p className="text-sm text-gray-500 leading-relaxed" data-id="element-1133">
                                    {step.desc}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Reveal>;
              })}
              </div>
            </div>

            {/* Mobile Vertical */}
            <div className="md:hidden relative" data-id="element-1134">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" data-id="element-1135" />
              <div className="space-y-4" data-id="element-1136">
                {c.steps.map((step, i) => {
                const StepIcon = stepIcons[i];
                const num = String(i + 1).padStart(2, '0');
                return <Reveal key={num} direction="fade" data-id="element-1137">
                      <div className="relative flex gap-4" data-id="element-1138">
                        <div className="w-8 flex-shrink-0 flex justify-center relative z-10" data-id="element-1139">
                          <div className="w-2 h-2 rounded-full bg-[var(--color-tan)] mt-6" data-id="element-1140" />
                        </div>
                        <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 relative" data-id="element-1141">
                          <span className="absolute top-3 right-3 text-xs font-medium text-gray-400" data-id="element-1142">
                            {num}
                          </span>
                          <div className="flex items-center gap-3 pr-8" data-id="element-1143">
                            <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-1144">
                              <StepIcon className="w-4 h-4 text-[var(--color-tan)]" data-id="element-1145" />
                            </div>
                            <div className="flex-1" data-id="element-1146">
                              <h3 className="text-sm font-semibold text-[var(--color-text-dark)] mb-0.5" data-id="element-1147">
                                {step.title}
                              </h3>
                              <p className="text-xs text-gray-500" data-id="element-1148">
                                {step.desc}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Reveal>;
              })}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* FORM + LEGAL SECTION */}
      <div className="cv-lazy-section" data-id="element-1149">
        <section id="offshore-form" className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-1150">
          <div className="max-w-4xl mx-auto" data-id="element-1151">
            <Reveal direction="fade" data-id="element-1152">
              <div className="text-center mb-8" data-id="element-1153">
                <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white" data-id="element-1154">
                  <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1155">
                    {c.formBadge}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-1156">
                  {c.formTitle}
                </h2>
                <p className="text-base text-[var(--color-text-light)] max-w-xl mx-auto" data-id="element-1157">
                  {c.formDesc}
                </p>
              </div>
            </Reveal>

            <Reveal delay={100} direction="up" data-id="element-1158">
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8" data-id="element-1159">
                {submitStatus === 'success' && <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-id="element-1160">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" data-id="element-1161" />
                    <span className="font-medium" data-id="element-1162">{c.formSuccess}</span>
                  </div>}
                {submitStatus === 'error' && <div className="mb-6 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-1163">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" data-id="element-1164" />
                    <span data-id="element-1165">
                      {c.formError}
                      <a href="mailto:info@elvora.consulting" className="underline font-medium" data-id="element-1166">
                        info@elvora.consulting
                      </a>
                    </span>
                  </div>}

                <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1167">
                  <div data-id="element-1168">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1169">
                      {c.labelFullName} <span className="text-red-500" data-id="element-1170">*</span>
                    </label>
                    <input type="text" required value={fullName} onChange={e => setFullName(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1171" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1172">
                    <div data-id="element-1173">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1174">
                        {c.labelPhone} <span className="text-red-500" data-id="element-1175">*</span>
                      </label>
                      <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors placeholder:text-gray-400" data-id="element-1176" />
                    </div>
                    <div data-id="element-1177">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1178">
                        {c.labelEmail} <span className="text-red-500" data-id="element-1179">*</span>
                      </label>
                      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1180" />
                    </div>
                  </div>

                  <div data-id="element-1181">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1182">
                      {c.labelServiceType}{' '}
                      <span className="text-red-500" data-id="element-1183">*</span>
                    </label>
                    <select required value={serviceType} onChange={e => setServiceType(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={selectStyle} data-id="element-1184">
                      <option value="" data-id="element-1185">{c.selectService}</option>
                      {c.serviceOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1186">
                          {opt.label}
                        </option>)}
                    </select>
                  </div>

                  <div className="flex justify-center pt-2" data-id="element-1187">
                    <button type="submit" disabled={submitting} className={`px-8 py-4 text-white text-base font-medium rounded-lg transition-all flex items-center gap-2 ${!submitting ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-1188">
                      {submitting ? <>
                          <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-1189" />{' '}
                          {c.submitting}
                        </> : <>
                          <CheckCircle className="w-5 h-5" data-id="element-1190" /> {c.submitBtn}
                        </>}
                    </button>
                  </div>
                </form>
              </div>
            </Reveal>

            <Reveal delay={150} direction="fade" data-id="element-1191">
              <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-6 md:p-8" data-id="element-1192">
                <div className="flex items-center gap-3 mb-6" data-id="element-1193">
                  <ShieldIcon className="w-6 h-6 text-[var(--color-tan)]" data-id="element-1194" />
                  <h3 className="text-lg font-bold text-[var(--color-text-dark)]" data-id="element-1195">
                    {c.legalTitle}
                  </h3>
                </div>
                <div className="space-y-6 text-sm text-[var(--color-text-light)]" data-id="element-1196">
                  <p data-id="element-1197">{c.legalText}</p>
                  <div data-id="element-1198">
                    <h4 className="font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-1199">
                      {c.refundTitle}
                    </h4>
                    <p data-id="element-1200">{c.refundText}</p>
                  </div>
                  <div data-id="element-1201">
                    <h4 className="font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-1202">
                      {c.dataTitle}
                    </h4>
                    <p data-id="element-1203">
                      {c.dataText}
                      <a href="mailto:info@elvora.consulting" className="text-[var(--color-tan)] hover:underline" data-id="element-1204">
                        info@elvora.consulting
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>

      {/* CONTACT SECTION */}
      <div className="cv-lazy-section-sm" data-id="element-1205">
        <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-dark)]" data-id="element-1206">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1207">
            <Reveal direction="fade" data-id="element-1208">
              <div className="inline-block px-4 py-1.5 border border-gray-600 rounded-full mb-6" data-id="element-1209">
                <span className="text-sm font-medium text-white" data-id="element-1210">
                  {c.contactBadge}
                </span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1211">
                {c.contactTitle}
              </h2>
              <p className="text-lg text-gray-400 mb-8" data-id="element-1212">{c.contactDesc}</p>
            </Reveal>
            <Reveal delay={100} direction="scale" data-id="element-1213">
              <div className="flex flex-wrap justify-center gap-4" data-id="element-1214">
                <a href="https://t.me/elvora_consulting" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-1215">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-1216">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-1217" />
                  </svg>
                  <span className="text-sm font-medium" data-id="element-1218">Telegram</span>
                </a>
                <a href="https://api.whatsapp.com/send/?phone=380731356233" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-1219">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-1220">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" data-id="element-1221" />
                  </svg>
                  <span className="text-sm font-medium" data-id="element-1222">WhatsApp</span>
                </a>
                <a href="mailto:info@elvora.consulting" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-1223">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1224">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-1225" />
                  </svg>
                  <span className="text-sm font-medium" data-id="element-1226">
                    info@elvora.consulting
                  </span>
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </div>

      <Footer hideSocials data-id="element-1227" />
    </div>;
}